"use client";

import { Button } from "@/app/(public)/shared/ui/Button";
import { Sparks, Xmark } from "iconoir-react";
import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useRef, useState } from "react";

// The demo modal (contact + qualification + calendar) only mounts on user interaction, so it's
// code-split out of the eager landing-page bundle it would otherwise bloat with the phone/country
// UI, the Supabase client, and the fetch-ui form primitives it transitively pulls in.
const DemoModal = dynamic(() => import("./DemoModal").then((m) => m.DemoModal), {
  ssr: false,
  // First open fetches the chunk over the network; show an immediate backdrop + spinner so the
  // click doesn't feel like it hung. Kept dependency-free (no FetchModal import) to preserve the
  // code-split — subsequent opens are instant from cache.
  loading: () => (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        role="status"
        aria-label="Loading"
        className="h-8 w-8 animate-spin rounded-full border-2 border-white/40 border-t-white"
      />
    </div>
  ),
});

// VAPI import - only load when needed
let Vapi: any = null;
if (typeof window !== "undefined") {
  import("@vapi-ai/web").then((module) => {
    Vapi = module.default;
  });
}

// Base modal props that all modals share
interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: "sm" | "md" | "lg";
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  className?: string;
}

// Content-specific props for different modal types
interface ContentModalProps extends BaseModalProps {
  type: "content";
  children: React.ReactNode;
  footer?: React.ReactNode;
}

interface CalModalProps extends BaseModalProps {
  type: "cal";
  calLink?: string;
  fallbackText?: string;
}

interface EmailModalProps extends BaseModalProps {
  type: "email";
  emailThreads?: Array<{
    threadId: string;
    subject: string;
    isUnread?: boolean;
    message: {
      messageId: string;
      from: {
        name?: string;
        email: string;
      };
      to: Array<{
        name?: string;
        email: string;
      }>;
      cc?: Array<{
        name?: string;
        email: string;
      }>;
      subject: string;
      body: string;
      timestamp: string;
      isFromNowadays?: boolean;
    };
  }>;
}

interface VapiModalProps extends BaseModalProps {
  type: "vapi";
  // No external props needed - VAPI is self-contained
}

interface ProposalModalProps extends BaseModalProps {
  type: "proposal";
  mockData?: any;
}

// Union type for all modal props
type UnifiedModalProps = ContentModalProps | CalModalProps | EmailModalProps | VapiModalProps | ProposalModalProps;

// Size configurations
const sizeClasses = {
  sm: "w-[95%] max-w-3xl", // Standard content modals - 95% width on mobile, max 3xl on desktop
  md: "w-[98%] max-w-5xl", // Complex content (VAPI, email, proposals) - 98% width on mobile, max 5xl on desktop
  lg: "max-w-none w-[98%] h-[90%]", // Video, cal.com, fullscreen content - 98% width, 90% height
};

export const UnifiedModal: React.FC<UnifiedModalProps> = (props) => {
  const {
    isOpen,
    onClose,
    title,
    subtitle,
    size,
    showCloseButton = true,
    closeOnBackdrop = true,
    closeOnEscape = true,
    className = "",
    type,
  } = props;

  // Smart default sizing based on modal type
  const getDefaultSize = (modalType: string): "sm" | "md" | "lg" => {
    switch (modalType) {
      case "cal":
        return "lg"; // Cal.com needs maximum space
      case "vapi":
        return "md"; // Voice interface needs more space for controls
      case "email":
      case "proposal":
        return "md"; // Content-heavy modals need more space
      default:
        return "sm";
    }
  };

  const modalSize = size || getDefaultSize(type);

  // Handle body scroll lock
  React.useEffect(() => {
    if (!isOpen) return;
    // The cal modal renders its own FetchModal, which manages scroll lock itself.
    if (type === "cal") return;

    // Save current scroll position
    const scrollY = window.scrollY;

    // Lock body scroll
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      // Restore body scroll
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Handle escape key
  React.useEffect(() => {
    if (!closeOnEscape || !isOpen) return;
    // The cal modal's FetchModal handles its own escape-to-close.
    if (type === "cal") return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // The cal modal is fully self-contained: it renders its own FetchModal with per-step adaptive
  // sizing (compact card for the intake form, full-screen for the calendar iframe), so it bypasses
  // the generic shell below entirely.
  if (type === "cal") {
    const calProps = props as CalModalProps;
    return <DemoModal isOpen={isOpen} onClose={onClose} title={title} subtitle={subtitle} calLink={calProps.calLink} />;
  }

  // Render content based on modal type
  const renderContent = () => {
    switch (type) {
      case "content":
        return (props as ContentModalProps).children;

      case "email":
        return <EmailContent {...(props as EmailModalProps)} />;

      case "vapi":
        return <VapiContent {...(props as VapiModalProps)} />;

      case "proposal":
        return <ProposalContent {...(props as ProposalModalProps)} />;

      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (type === "content" && (props as ContentModalProps).footer) {
      return (props as ContentModalProps).footer;
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className={`relative bg-white rounded-xl shadow-2xl overflow-hidden ${
          type === "email" ? "mx-2 md:mx-4" : "mx-4"
        } ${sizeClasses[modalSize]} ${className}`}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
            <div>
              {title && <h2 className="text-lg md:text-xl font-semibold text-gray-900">{title}</h2>}
              {subtitle && <p className="text-xs md:text-sm text-gray-600 mt-1">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
                <Xmark className="w-4 md:w-5 h-4 md:h-5" />
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="max-h-[calc(90vh-100px)] overflow-y-auto">{renderContent()}</div>

        {/* Footer */}
        {renderFooter() && <div className="border-t border-gray-200 p-4 md:p-6 bg-gray-50">{renderFooter()}</div>}
      </div>
    </div>
  );
};

// VAPI Hook - moved from Feature2.tsx for better encapsulation
const useVapiAssistant = (apiKey: string, config: any) => {
  const vapiRef = useRef<any>(null);
  const [state, setState] = useState({
    isActive: false,
    isLoading: false,
    error: null as string | null,
    volumeLevel: 0,
    transcript: "",
    messages: [] as any[],
  });

  const startConversation = useCallback(async () => {
    if (!apiKey) {
      setState((prev) => ({ ...prev, error: "No API key provided" }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Dynamically import VAPI if not already loaded
      if (!Vapi) {
        const VapiModule = await import("@vapi-ai/web");
        Vapi = VapiModule.default;
      }

      if (!vapiRef.current) {
        vapiRef.current = new Vapi(apiKey);

        // Set up event listeners
        vapiRef.current.on("call-start", () => {
          setState((prev) => ({ ...prev, isActive: true, isLoading: false }));
        });

        vapiRef.current.on("call-end", () => {
          setState((prev) => ({ ...prev, isActive: false, isLoading: false }));
        });

        vapiRef.current.on("error", (error: any) => {
          setState((prev) => ({
            ...prev,
            isActive: false,
            isLoading: false,
            error: error?.message || "Voice assistant error",
          }));
        });

        vapiRef.current.on("volume-level", (level: number) => {
          setState((prev) => ({ ...prev, volumeLevel: level }));
        });

        vapiRef.current.on("speech-start", () => {
          // User started speaking
        });

        vapiRef.current.on("speech-end", () => {
          // User stopped speaking
        });
      }

      const assistantConfig = {
        voice: {
          provider: "vapi" as const,
          voiceId: "Elliot" as const,
        },
        model: {
          provider: "openai" as const,
          model: "gpt-4o" as const,
          messages: [
            {
              role: "system" as const,
              content: config.systemPrompt || "You are a helpful AI assistant.",
            },
          ],
        },
        firstMessage: config.firstMessage || "Hello! How can I help you today?",
        transcriber: {
          provider: "deepgram" as const,
          model: "nova-3" as const,
          language: "en" as const,
        },
      };

      await vapiRef.current.start(assistantConfig);
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error?.message || "Failed to start conversation",
      }));
    }
  }, [apiKey, config]);

  const endConversation = useCallback(async () => {
    if (!vapiRef.current) return;

    try {
      vapiRef.current.stop();
      setState((prev) => ({ ...prev, isActive: false }));
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        error: error?.message || "Failed to end conversation",
      }));
    }
  }, []);

  return {
    state,
    actions: { startConversation, endConversation, updateConfig: () => {} },
  };
};

// Helper component to format markdown-style text without a markdown renderer
const FormattedEmailContent: React.FC<{ content: string }> = ({ content }) => {
  const formatContent = (text: string) => {
    const lines = text.split("\n");
    const elements: React.ReactNode[] = [];
    let currentIndex = 0;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Skip empty lines but add spacing
      if (line.trim() === "") {
        elements.push(<div key={`space-${currentIndex++}`} className="h-4" />);
        i++;
        continue;
      }

      // Handle horizontal rules (---)
      if (line.trim() === "---") {
        elements.push(
          <div key={`hr-${currentIndex++}`} className="my-6">
            <div className="border-t border-gray-300" />
          </div>
        );
        i++;
        continue;
      }

      // Handle headers (###)
      if (line.startsWith("### ")) {
        const headerText = line.substring(4);
        elements.push(
          <h3 key={`h3-${currentIndex++}`} className="text-lg font-semibold text-gray-900 mt-6 mb-3">
            {headerText}
          </h3>
        );
        i++;
        continue;
      }

      // Handle tables (look for | characters)
      if (line.includes("|") && line.trim().startsWith("|")) {
        const tableRows: string[] = [];
        let j = i;

        // Collect all table rows
        while (j < lines.length && lines[j].includes("|") && lines[j].trim().startsWith("|")) {
          tableRows.push(lines[j]);
          j++;
        }

        if (tableRows.length > 0) {
          elements.push(
            <div key={`table-${currentIndex++}`} className="my-4 overflow-x-auto">
              <table className="min-w-full border border-gray-300 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    {tableRows[0]
                      .split("|")
                      .slice(1, -1)
                      .map((header, idx) => (
                        <th
                          key={idx}
                          className="px-4 py-2 text-left font-semibold text-gray-900 border-b border-gray-300"
                        >
                          {header.trim()}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows
                    .slice(1)
                    .filter((row) => !row.includes("---"))
                    .map((row, rowIdx) => (
                      <tr key={rowIdx} className="border-b border-gray-200">
                        {row
                          .split("|")
                          .slice(1, -1)
                          .map((cell, cellIdx) => (
                            <td key={cellIdx} className="px-4 py-2 text-gray-700">
                              {formatInlineText(cell.trim())}
                            </td>
                          ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          );
          i = j;
          continue;
        }
      }

      // Handle bullet points (-)
      if (line.trim().startsWith("- ")) {
        const bulletText = line.trim().substring(2);
        elements.push(
          <div key={`bullet-${currentIndex++}`} className="flex items-start gap-2 mb-2">
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
            <span className="text-gray-700">{formatInlineText(bulletText)}</span>
          </div>
        );
        i++;
        continue;
      }

      // Regular paragraphs
      elements.push(
        <p key={`p-${currentIndex++}`} className="text-gray-700 mb-4 leading-relaxed">
          {formatInlineText(line)}
        </p>
      );
      i++;
    }

    return elements;
  };

  const formatInlineText = (text: string) => {
    const parts: React.ReactNode[] = [];
    let keyIndex = 0;

    // Handle bold text (**text**)
    const boldRegex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        parts.push(formatItalicText(beforeText, keyIndex++));
      }

      // Add bold text
      parts.push(
        <strong key={`bold-${keyIndex++}`} className="font-semibold text-gray-900">
          {match[1]}
        </strong>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      parts.push(formatItalicText(remainingText, keyIndex++));
    }

    return parts.length > 0 ? parts : text;
  };

  const formatItalicText = (text: string, baseKey: number) => {
    const parts: React.ReactNode[] = [];
    const italicRegex = /\*(.*?)\*/g;
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = italicRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Add italic text
      parts.push(
        <em key={`italic-${baseKey}-${keyIndex++}`} className="italic text-gray-700">
          {match[1]}
        </em>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return <div className="space-y-0">{formatContent(content)}</div>;
};

// Content components extracted from existing modals
const EmailContent: React.FC<EmailModalProps> = ({ emailThreads = [] }) => {
  const [selectedThreadIndex, setSelectedThreadIndex] = React.useState(0);

  // Default email threads - 3 separate threads
  const defaultEmailThreads = [
    {
      threadId: "thread_1",
      subject: "RFP for Pied Piper – May 2026 Program",
      isUnread: true,
      message: {
        messageId: "msg_1",
        from: { name: "Nowadays Team", email: "events@nowadays.ai" },
        to: [{ name: "Hotel Team", email: "sales@liveaquaresort.com" }],
        cc: [],
        subject: "RFP for Pied Piper – May 2026 Program",
        body: `Dear Hotel Team,

We're reaching out on behalf of **Pied Piper**, a fast-growing leader in enterprise compression technology, to source a resort partner for their upcoming Q2 2026 program. We're currently exploring potential properties in **Costa Rica** (top choice) and **Punta Cana** (second choice), and would love to receive a detailed proposal from your hotel.

**About Pied Piper**

Pied Piper is known for its groundbreaking data compression platform, trusted by enterprises around the world. The company hosts annual gatherings to bring together key clients, executives, and partners in a dynamic, luxury setting that blends relaxation with connection.

**Program Overview**

- **Dates**: April – June 2026 (please provide your available Wednesday–Sunday options)
- **Rooms**: 70 rooms for 4 nights
- **Occupancy**: Double (2 adults per room)
- **Pattern**: Wednesday check-in, Sunday check-out
- **Preferred Destinations**: Costa Rica (priority), Punta Cana (backup)

**Event Requirements**

- **Registration/Welcome Desk** (Day 1)
- **Welcome Dinner** (Night 1)
- **Daytime Meeting Block** (Day 2, a few hours)
- **Farewell Dinner** (Final Night)
- **Dedicated Office + Storage Space** throughout the stay

**Program History**

This event has previously been held at:
- *Secrets Playa Blanca, Cancun* – 2024 & 2025
- *Nobu Los Cabos* – 2023

**Proposal Request**

We kindly ask for a full proposal that includes:
- Room rates, availability, and detailed cost breakdown
- Event space options and pricing (please waive setup fees if possible)
- Food & Beverage package details
- Any added-value amenities or resort inclusions
- Grand total estimate covering all major costs

**Site Visit & Decision Timeline**

Site inspections will take place in **April–May 2025**, with a final decision to follow shortly after.

If you have any questions or need more context, we're happy to hop on a call. We look forward to reviewing your proposal and potentially partnering on this exciting program!

Warm regards,

*The Nowadays Team*
on behalf of Pied Piper`,
        timestamp: "3 days ago",
        isFromNowadays: true,
      },
    },
    {
      threadId: "thread_2",
      subject: "Pied Piper – Company Meet Up RFP Summary",
      isUnread: true,
      message: {
        messageId: "msg_2",
        from: { name: "Nowadays Team", email: "events@nowadays.ai" },
        to: [{ name: "Hotel Team", email: "sales@liveaquaresort.com" }],
        cc: [],
        subject: "Pied Piper – Company Meet Up RFP Summary",
        body: `**Pied Piper – Company Meet Up RFP Summary**

**About Pied Piper**

Pied Piper is a fully remote company helping innovative teams scale smarter, move faster, and maintain seamless compliance. We have a globally distributed team of approximately 85 employees, primarily based in the U.S. and Ireland.

**Timing Options**

We anticipate ~70 participants and are exploring two potential windows:

- **Fall 2025**: Mid-October to Mid-November
    *Please avoid October 3–6 and U.S. Halloween*

**OR**

- **Spring 2026**: Early March to Early April
    *Please avoid Easter and Passover*

**Location Preferences**

- Within **1 hour of a major city**
- Located in a **non-urban / quiet environment**
- Preference to **be the only group onsite**

**Estimated Room Nights**

| **Day** | **Room Nights** |
| Arrival (Sunday) | 10 |
| Day 0 (Monday) | 70 |
| Day 1 (Tuesday) | 70 |
| Day 2 (Wednesday) | 70 |
| Day 3 (Thursday) | 70 |

*Alternate day patterns may be considered for better rates.*

**Meeting & Event Requirements**

**Day 0 (Monday)**
- **All Day** – Team Office Space
- **7:00–9:00 PM** – Welcome Reception (55 pax, cocktail rounds, hors d'oeuvres, beer & wine)

**Day 1 (Tuesday)**
- **All Day** – Team Office Space
- **8:00–8:30 AM** – Breakfast (70 pax, rounds)
- **8:30–12:00 PM** – General Session (70 pax, crescent rounds)
- **12:00–1:00 PM** – Buffet Lunch (70 pax)
- **1:00–5:00 PM** – General Session
- **Evening** – Group Dinner (onsite or offsite)

**Day 2 (Wednesday)**
- **All Day** – Team Office Space
- **8:00–8:30 AM** – Breakfast (70 pax, rounds)
- **8:30–10:00 AM** – General Session
- **10:00–12:00 PM** – Breakouts (3–4 rooms, 15–20 pax each)
- **12:00–1:00 PM** – Buffet Lunch
- **1:00–3:00 PM** – General Session
- **3:00–5:00 PM** – Breakouts (3–4 rooms)
- **Evening** – Small Group Dinners

**Day 3 (Thursday)**
- **All Day** – Team Office Space
- **8:00–8:30 AM** – Breakfast
- **8:30–10:00 AM** – General Session
- **10:00–12:00 PM** – Breakouts
- **12:00–1:00 PM** – Buffet Lunch
- **1:00–3:00 PM** – General Session or Optional Group Activity
- **3:00–5:00 PM** – Breakouts
- **6:00–6:45 PM** – Closing Reception
- **7:00–9:00 PM** – Awards Dinner (70 pax, rounds)

**AV & Tech Requirements**

- Sound system
- Small stage
- Projector & screen
- Slide advancer / clicker
- Up to 6 lapel microphones
- Podium (for awards dinner)
- AV/IT control table at the back of the room

**Proposal Details**

- Submit your **proposal as soon as possible**
- We are especially interested in **competitive group packages** and **exclusive use of property (full buyout preferred)**`,
        timestamp: "2 days ago",
        isFromNowadays: true,
      },
    },
    {
      threadId: "thread_3",
      subject: "Request for Availability & Rates – March 2025 Corporate Offsite",
      isUnread: true,
      message: {
        messageId: "msg_3",
        from: { name: "Nowadays Team", email: "events@nowadays.ai" },
        to: [{ name: "Sales Team", email: "sales@mountainresort.com" }],
        cc: [],
        subject: "Request for Availability & Rates – March 2025 Corporate Offsite",
        body: `Hi Sales Team,

We're reaching out on behalf of a client who is planning a corporate offsite in **March 2025** and is evaluating potential venues. We're an **IATA-certified travel agency**, and we're assisting with sourcing and coordination for this event.

Our client is a **software company based in the Bay Area, California**, and prefers to remain anonymous until the contracting phase. They are interested in hosting their team meeting at a property that can support the following program:

**Event Overview**

- **Event Name**: Corporate Offsite 2025
- **Event Type**: Team offsite
- **Check-in**: March 8, 2025
- **Check-out**: March 16, 2025
- **Attendees**: 100 people
- **Rooms Requested**: 48 total rooms
    - **45 shared occupancy** (2 beds per room)
    - **3 single occupancy rooms**

**Meeting Requirements**

- **Days 1–2**: Main meeting space for **100 pax in classroom style**
- **Days 3–5**: **3 breakout rooms**, each to accommodate up to **40 pax**

**F&B Requirements (Monday–Friday)**

- Morning break
- Lunch
- Afternoon break

**Final Clarifications Needed**

To finalize our shortlist, we'd appreciate if you could confirm the following:

A. **Main meeting space for 100 pax**:
- Can your space accommodate classroom-style seating with a screen and reliable Wi-Fi?
- Could you please confirm availability and share **photos of the space**?

B. **Breakout space**:
- Do you have **three rooms** available that can each accommodate **up to 40 attendees**?

C. **Dining**:
- Is there **restaurant space onsite** that can seat **all 100 attendees at once**?
- If so, could you please share **photos of the dining area**?

This client is also evaluating venues for **future repeat business**—they would ideally like to return to the same property annually, pending a successful first event.

Thank you so much for your time! We look forward to your availability and proposal.

Cheers,

Nowadays`,
        timestamp: "1 day ago",
        isFromNowadays: true,
      },
    },
  ];

  const threads = emailThreads.length > 0 ? emailThreads : defaultEmailThreads;
  const selectedThread = threads[selectedThreadIndex];
  const selectedMessage = selectedThread?.message;

  return (
    <div className="flex flex-col lg:flex-row h-[calc(80vh-100px)]">
      {/* Mobile Thread List - Horizontal scroll at top */}
      <div className="lg:hidden">
        <div className="px-4 py-3 border-b border-gray-300 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <h3 className="font-medium text-gray-900 text-sm">Inbox</h3>
              <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">{threads.length}</span>
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <span>Swipe</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
        <div className="relative">
          {/* Scroll container with enhanced UX */}
          <div className="flex overflow-x-auto border-b border-gray-200 bg-white scrollbar-hide snap-x snap-mandatory">
            {threads.map((thread, index) => (
              <button
                key={thread.threadId}
                onClick={() => setSelectedThreadIndex(index)}
                className={`flex-shrink-0 px-4 py-3 min-w-[300px] max-w-[300px] text-left border-r border-gray-200 last:border-r-0 transition-all duration-200 snap-start ${
                  selectedThreadIndex === index
                    ? "bg-blue-50 border-l-4 border-l-blue-500 shadow-sm"
                    : "bg-white hover:bg-gray-50 border-l-4 border-l-transparent hover:shadow-sm"
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Sender Avatar */}
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                    {thread.message.from.name
                      ? thread.message.from.name.charAt(0).toUpperCase()
                      : thread.message.from.email.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {thread.message.from.name || "Nowadays Team"}
                      </span>
                      {thread.isUnread && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>}
                    </div>

                    <h4 className="text-sm text-gray-800 font-normal leading-tight mb-1 line-clamp-2">
                      {thread.subject}
                    </h4>

                    <p className="text-xs text-gray-500 line-clamp-1 leading-relaxed mb-2">
                      {thread.message.body.split("\n")[0].substring(0, 60)}...
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{thread.message.timestamp}</span>
                      <div className="w-3 h-3 text-orange-400">
                        <Sparks className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Left fade indicator */}
          <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none"></div>

          {/* Right fade indicator */}
          <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>

          {/* Thread position indicators */}
          <div className="flex justify-center py-2 bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-1">
              {threads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedThreadIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    selectedThreadIndex === index ? "bg-blue-500" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar - Thread List */}
      <div className="hidden lg:flex lg:w-1/3 border-r border-gray-200 flex-col bg-gray-50">
        <div className="px-4 py-3 border-b border-gray-300 bg-white">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <h3 className="font-medium text-gray-900 text-sm">Inbox</h3>
            <span className="ml-auto bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">{threads.length}</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-white">
          {threads.map((thread, index) => (
            <div
              key={thread.threadId}
              onClick={() => {
                setSelectedThreadIndex(index);
              }}
              className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedThreadIndex === index
                  ? "bg-blue-50 border-l-4 border-l-blue-500"
                  : "border-l-4 border-l-transparent"
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Sender Avatar */}
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                  {thread.message.from.name
                    ? thread.message.from.name.charAt(0).toUpperCase()
                    : thread.message.from.email.charAt(0).toUpperCase()}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {thread.message.from.name || "Nowadays Team"}
                    </span>
                    {thread.isUnread && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>}
                  </div>

                  <h4 className="text-sm text-gray-800 font-normal leading-tight mb-1 line-clamp-2">
                    {thread.subject}
                  </h4>

                  <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                    {thread.message.body.split("\n")[0].substring(0, 80)}...
                  </p>

                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">{thread.message.timestamp}</span>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 text-orange-400">
                        <Sparks className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message View */}
      <div className="flex-1 flex flex-col">
        {selectedThread && selectedMessage && (
          <>
            {/* Email Header - Gmail Style */}
            <div className="bg-white border-b border-gray-200">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h1 className="text-lg font-normal text-gray-900 truncate pr-4">{selectedThread.subject}</h1>
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium flex-shrink-0">
                    <Sparks className="w-3 h-3" />
                    <span>AI Generated</span>
                  </div>
                </div>
              </div>

              {/* Email Toolbar */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3 text-sm">
                  <button className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-200 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      />
                    </svg>
                    Reply
                  </button>
                  <button className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-200 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6"
                      />
                    </svg>
                    Forward
                  </button>
                  <button className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-200 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                  <div className="flex-1"></div>
                  <button className="p-1 text-gray-600 hover:bg-gray-200 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Message Content */}
            <div className="flex-1 overflow-y-auto bg-white">
              <div className="p-6 max-w-none">
                {/* Email Message */}
                <div className="bg-white">
                  {/* Sender Info - Gmail Style */}
                  <div className="flex items-start gap-3 mb-4">
                    {/* Avatar */}
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                      {selectedMessage.from.name
                        ? selectedMessage.from.name.charAt(0).toUpperCase()
                        : selectedMessage.from.email.charAt(0).toUpperCase()}
                    </div>

                    {/* Sender Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col md:flex-row md:items-center md:gap-2 mb-1">
                            <span className="font-medium text-gray-900 text-sm">
                              {selectedMessage.from.name || selectedMessage.from.email}
                            </span>
                            <span className="text-gray-500 text-sm">&lt;{selectedMessage.from.email}&gt;</span>
                          </div>

                          <div className="text-sm text-gray-600 space-y-1">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500">to</span>
                              <span>{selectedMessage.to.map((t) => t.name || t.email).join(", ")}</span>
                              <button className="text-gray-400 hover:text-gray-600 ml-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </button>
                            </div>
                            {selectedMessage.cc && selectedMessage.cc.length > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="text-gray-500">cc</span>
                                <span>{selectedMessage.cc.map((c) => c.name || c.email).join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                              />
                            </svg>
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message Body */}
                  <div className="ml-12 border-l-2 border-gray-100 pl-4 mt-4">
                    <div className="prose prose-sm max-w-none">
                      <FormattedEmailContent content={selectedMessage.body} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Reply Section */}
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                      />
                    </svg>
                    Reply
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6"
                      />
                    </svg>
                    Forward
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const VapiContent: React.FC<VapiModalProps> = () => {
  // Use internal VAPI hook with default configuration
  const { state: vapiState, actions: vapiActions } = useVapiAssistant(process.env.NEXT_PUBLIC_VAPI_API_KEY || "", {
    systemPrompt: `You are Victor, an AI voice assistant created by Nowadays to demonstrate our advanced AI phone call capabilities.

IMPORTANT: This is a DEMONSTRATION ONLY. You are not making a real business call - you are showcasing Nowadays' AI technology to potential customers who are testing our platform.

Your role in this demo:
- You represent "Victor from Nowadays" following up on a fictional RFP (Request for Proposal) sent to a hotel
- Demonstrate natural conversation flow, professional tone, and intelligent responses
- Show how AI can handle complex business conversations with nuance and context
- Keep the conversation engaging but concise (responses under 25 words unless asked for details)

Demo scenario context:
- You supposedly sent an RFP yesterday for a corporate event
- You're following up to check if they received it and answer any questions
- Be prepared to discuss event details like dates, guest count, catering, AV needs, etc.
- If they ask for specifics, you can improvise reasonable corporate event details

Conversation guidelines:
- Be professional, friendly, and helpful
- Demonstrate active listening by referencing what they say
- Show problem-solving abilities if they mention concerns
- End gracefully when the conversation naturally concludes
- If they seem confused, gently remind them this is a demo of Nowadays' AI technology

Remember: Your goal is to impress the person testing this demo with how natural and capable AI phone conversations can be.`,
    firstMessage:
      "Hi there! This is Victor calling from Nowadays. I sent over an RFP yesterday for a corporate event and wanted to follow up - did your team have a chance to review it?",
    maxDurationSeconds: 180, // 3 minutes for demo
  });

  const handleStartConversation = async () => {
    if (!process.env.NEXT_PUBLIC_VAPI_API_KEY) {
      alert("VAPI API key not configured. Please add NEXT_PUBLIC_VAPI_API_KEY to your environment variables.");
      return;
    }
    try {
      await vapiActions.startConversation();
    } catch (error) {
      console.error("Failed to start voice conversation:", error);
      alert("Failed to start voice conversation. Please check your microphone permissions and try again.");
    }
  };

  const handleEndConversation = async () => {
    await vapiActions.endConversation();
  };

  return (
    <div className="p-4 md:p-6">
      <div className="text-center mb-6">
        <p className="text-gray-600">
          Experience our AI voice assistant that can handle venue inquiries and gather information automatically.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className={`w-8 h-8 bg-indigo-600 rounded-full ${vapiState?.isActive ? "animate-pulse" : ""}`}></div>
            </div>
            <h4 className="font-medium text-gray-900 mb-2">
              {vapiState?.isActive ? "Call Active" : "AI Voice Assistant Demo"}
            </h4>
            <p className="text-sm text-gray-600">
              {vapiState?.isActive
                ? "AI assistant is listening and ready to respond"
                : "Experience our AI assistant that can handle venue inquiries automatically"}
            </p>
          </div>

          {!vapiState?.isActive ? (
            <div className="space-y-3 mt-6">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">Make sure your microphone is enabled</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">Speak clearly and wait for responses</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">Try asking about venue availability or pricing</span>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <div className="flex items-center justify-center gap-1 h-12">
                {Array.from({ length: 12 }, (_, i) => {
                  // Create a waveform effect with different heights based on voice level and position
                  const baseHeight = 8;
                  const maxHeight = 40;
                  const volumeMultiplier = vapiState.volumeLevel || 0;

                  // Create a wave pattern - center bars are taller
                  const distanceFromCenter = Math.abs(i - 5.5);
                  const centerMultiplier = 1 - distanceFromCenter / 6;

                  // Add some randomness for more natural look
                  const randomMultiplier = 0.7 + Math.sin(Date.now() / 200 + i) * 0.3;

                  const height =
                    baseHeight + (maxHeight - baseHeight) * volumeMultiplier * centerMultiplier * randomMultiplier;

                  return (
                    <div
                      key={i}
                      className="bg-indigo-500 rounded-full transition-all duration-150 ease-out"
                      style={{
                        width: "3px",
                        height: `${Math.max(baseHeight, height)}px`,
                        opacity: volumeMultiplier > 0 ? 0.8 + volumeMultiplier * 0.2 : 0.3,
                      }}
                    />
                  );
                })}
              </div>
              <div className="text-center mt-2">
                <span className="text-xs text-indigo-600 font-medium">
                  {vapiState.volumeLevel > 0 ? "Listening..." : "Speak to begin"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {vapiState?.error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{vapiState.error}</p>
        </div>
      )}

      <div className="flex gap-3">
        {!vapiState?.isActive ? (
          <button
            onClick={handleStartConversation}
            disabled={vapiState?.isLoading}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {vapiState?.isLoading ? "Connecting..." : "Start Voice Call"}
          </button>
        ) : (
          <button
            onClick={handleEndConversation}
            className="flex-1 py-3 px-6 rounded-lg font-medium transition-colors text-white bg-red-600 hover:bg-red-700"
          >
            End Call
          </button>
        )}
      </div>
    </div>
  );
};

const ProposalContent: React.FC<ProposalModalProps> = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    updateIsMobile(); // Set initial value
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  return (
    <div className="p-6 md:p-8">
      {/* Two Card Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Card - Original PDF */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative h-[35vh] lg:h-[70vh]">
          {/* Header */}
          <div className="bg-landing-dark px-6 py-4 flex justify-between items-center">
            <div>
              <h4 className="text-landing-white font-semibold text-sm">Original Proposal</h4>
              <p className="text-gray-300 text-xs">Messy PDF format</p>
            </div>
            {/* Action buttons */}
            <div className="flex gap-2">
              <a
                href="/supabase-recommended/Live_Aqua_Proposal.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
              >
                Preview
              </a>
              <a
                href="/supabase-recommended/Live_Aqua_Proposal.pdf"
                download="Live_Aqua_Proposal.pdf"
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
              >
                Download
              </a>
            </div>
          </div>

          {/* PDF Preview */}
          <div className="h-[calc(100%-72px)]">
            {isMobile ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-600 p-4">
                <p className="mb-4">
                  This PDF preview is not available on mobile devices. Please click "Preview" or "Download" to view it.
                </p>
              </div>
            ) : (
              <iframe
                src="/supabase-recommended/Live_Aqua_Proposal.pdf"
                className="w-full h-full bg-gray-100"
                title="Original Proposal PDF"
              />
            )}
          </div>
        </div>

        {/* Right Card - Our Clean Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-[35vh] lg:h-[70vh]">
          {/* Header */}
          <div className="bg-landing-button-primary px-6 py-4">
            <h4 className="text-white font-semibold text-sm">Nowadays Breakdown</h4>
            <p className="text-white/70 text-xs">Clean, organized breakdown</p>
          </div>

          {/* Content area */}
          <div className="h-[calc(100%-72px)] overflow-y-auto px-6 py-5">
            <div className="flex flex-col gap-8 h-full">
              {/* Quote Header */}
              <div className="flex flex-col gap-2">
                <div className="text-landing-primary text-tag-md">Quote</div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline gap-3">
                    <div className="text-gray-400 text-tag-lg line-through">$680,600</div>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <div className="text-landing-primary text-tag-xl">$496,800</div>
                    <div className="px-2 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded">
                      27% off Nowadays negotiated
                    </div>
                  </div>
                </div>
                <div className="text-landing-secondary text-body-sm">Total all-inclusive package</div>
              </div>

              {/* Details Section */}
              <div className="flex flex-col gap-4 flex-1">
                <div className="text-landing-primary text-tag-md">Details</div>
                <div className="flex flex-col gap-6">
                  {/* Guest Room Breakdown */}
                  <div className="flex flex-col gap-4">
                    <div className="text-landing-secondary text-body-sm">Guest Room Breakdown</div>
                    <div className="flex justify-between items-center md:items-start">
                      <div className="text-landing-primary text-body-sm md:flex-row flex flex-col">
                        <span>115 ROH Double × $285/night</span>
                        <span className="md:ml-1 md:inline block text-body-sm">× 8 nights</span>
                      </div>
                      <div className="text-landing-secondary text-body-sm">$261,900</div>
                    </div>
                    <div className="flex justify-between items-center md:items-start">
                      <div className="text-landing-primary text-body-sm md:flex-row flex flex-col">
                        <span>40 ROH Single × $533/night</span>
                        <span className="md:ml-1 md:inline block text-body-sm">× 8 nights</span>
                      </div>
                      <div className="text-landing-secondary text-body-sm">$170,500</div>
                    </div>
                    <div className="flex justify-between items-center md:items-start">
                      <div className="text-landing-primary text-body-sm md:flex-row flex flex-col">
                        <span>10 Fuego Suite Double × $376/night</span>
                        <span className="md:ml-1 md:inline block text-body-sm">× 8 nights</span>
                      </div>
                      <div className="text-landing-secondary text-body-sm">$30,100</div>
                    </div>
                    <div className="flex justify-between items-center md:items-start">
                      <div className="text-landing-primary text-body-sm md:flex-row flex flex-col">
                        <span>5 Fuego Suite Single × $715/night</span>
                        <span className="md:ml-1 md:inline block text-body-sm">× 8 nights</span>
                      </div>
                      <div className="text-landing-secondary text-body-sm">$28,600</div>
                    </div>
                    <div className="flex justify-between items-center md:items-start">
                      <div className="text-landing-primary text-body-sm md:flex-row flex flex-col">
                        <span>Staff rooms (5 rooms) × $142/night</span>
                        <span className="md:ml-1 md:inline block text-body-sm">× 8 nights</span>
                      </div>
                      <div className="text-landing-secondary text-body-sm">$5,700</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Complimentary rooms (5 rooms)</div>
                      <div className="text-landing-secondary text-body-sm">$0</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Taxes & Service Charges</div>
                      <div className="text-landing-secondary text-body-sm">Included</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-tag-sm">Room Total</div>
                      <div className="text-landing-primary text-tag-sm">$496,800</div>
                    </div>
                  </div>

                  <div className="h-px bg-zinc-300" />

                  {/* Meeting Space Breakdown */}
                  <div className="flex flex-col gap-4">
                    <div className="text-landing-secondary text-body-sm">Meeting Space Breakdown</div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">
                        General Session (Baltico/Adriatico + Mediterraneo)
                      </div>
                      <div className="text-landing-secondary text-body-sm">Included</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">
                        Breakout Rooms (Caspio, Indico, Cantabrico)
                      </div>
                      <div className="text-landing-secondary text-body-sm">Included</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-tag-sm">Meeting Total</div>
                      <div className="text-landing-primary text-tag-sm">$0</div>
                    </div>
                  </div>

                  <div className="h-px bg-zinc-300" />

                  {/* Food & Beverage Breakdown */}
                  <div className="flex flex-col gap-4">
                    <div className="text-landing-secondary text-body-sm">Food & Beverage Breakdown</div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">All meals (breakfast, lunch, dinner)</div>
                      <div className="text-landing-secondary text-body-sm">Included</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Premium bar & room service</div>
                      <div className="text-landing-secondary text-body-sm">Included</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-tag-sm">F&B Total</div>
                      <div className="text-landing-primary text-tag-sm">$0</div>
                    </div>
                  </div>

                  <div className="h-px bg-zinc-300" />

                  {/* Special Concessions */}
                  <div className="flex flex-col gap-4">
                    <div className="text-landing-secondary text-body-sm">Nowadays Negotiated Concessions</div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">5 complimentary rooms</div>
                      <div className="text-violet-600 text-body-sm font-medium">✓ Negotiated</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Meeting space fees waived</div>
                      <div className="text-violet-600 text-body-sm font-medium">✓ Negotiated</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Upgrade to premium suites</div>
                      <div className="text-violet-600 text-body-sm font-medium">✓ Negotiated</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Airport transfers included</div>
                      <div className="text-violet-600 text-body-sm font-medium">✓ Negotiated</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-primary text-body-sm">Late checkout available</div>
                      <div className="text-violet-600 text-body-sm font-medium">✓ Negotiated</div>
                    </div>
                  </div>

                  <div className="h-px bg-zinc-300" />

                  {/* Final Total */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-landing-primary text-tag-md">Total Package</div>
                      <div className="flex items-baseline gap-2">
                        <div className="text-landing-primary text-tag-lg">$496,800</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-landing-secondary text-body-sm">All-inclusive • 175 rooms • 8 nights</div>
                      <div className="px-2 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded">
                        $183,800 saved vs. standard rates
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper hook for managing multiple modals
export const useModalManager = () => {
  const [modals, setModals] = React.useState<Record<string, boolean>>({});

  const openModal = (modalId: string) => {
    setModals((prev) => ({ ...prev, [modalId]: true }));
  };

  const closeModal = (modalId: string) => {
    setModals((prev) => ({ ...prev, [modalId]: false }));
  };

  const isModalOpen = (modalId: string) => {
    return modals[modalId] || false;
  };

  return { openModal, closeModal, isModalOpen };
};
